import { useEffect, useRef, useState } from "react";
import { Map, Source, Layer } from "@vis.gl/react-maplibre";

import { POLYGON_OPACITY, POLYGON_STROKE_WIDTH } from "@/constants/map";

export const MapScreenshotRenderer = () => {
  const jobsRef = useRef([]);
  const currentRef = useRef(null);

  const [current, setCurrent] = useState(null);

  const process = () => {
    if (currentRef.current || jobsRef.current.length === 0) return;

    const next = jobsRef.current.shift();

    currentRef.current = next;
    setCurrent(next);
  };

  useEffect(() => {
    window.__MAP_SCREENSHOT_RENDERER__ = {
      render: (job) => {
        jobsRef.current.push(job);
        process();
      },
      process,
      reset: () => {
        jobsRef.current = [];
        currentRef.current = null;
        setCurrent(null);
      },
    };

    return () => {
      delete window.__MAP_SCREENSHOT_RENDERER__;
    };
  }, []);

  if (!current) return null;

  const sourceId = `source-${current.id}`;
  const layerId = `fill-${current.id}`;
  const outlineId = `outline-${current.id}`;
  const pointId = `point-${current.id}`;

  return (
    <div style={{ position: "absolute", left: -9999, top: -9999 }}>
      <Map
        key={current?.id}
        initialViewState={{ longitude: 0, latitude: 0, zoom: 1 }}
        style={{ width: 300, height: 200 }}
        mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"
        preserveDrawingBuffer
        onLoad={(e) => {
          const map = e.target;
          const { geojson } = current;

          if (geojson.geometry.type === "Point") {
            const [lng, lat] = geojson.geometry.coordinates;

            map.flyTo({
              center: [lng, lat],
              zoom: 15,
              duration: 0,
            });
          } else {
            // --- bounds ---
            const coords = [];
            const collect = (arr) =>
              arr.forEach(([lng, lat]) => coords.push([lng, lat]));

            if (geojson.geometry.type === "Polygon") {
              collect(geojson.geometry.coordinates[0]);
            }

            if (geojson.geometry.type === "MultiPolygon") {
              geojson.geometry.coordinates.forEach((poly) =>
                poly.forEach((ring) => collect(ring)),
              );
            }

            if (coords.length) {
              let minLng = coords[0][0],
                maxLng = coords[0][0];
              let minLat = coords[0][1],
                maxLat = coords[0][1];

              coords.forEach(([lng, lat]) => {
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
              });

              map.fitBounds(
                [
                  [minLng, minLat],
                  [maxLng, maxLat],
                ],
                { padding: 20, duration: 0 },
              );
            }
          }

          // --- скрин ---
          map.once("idle", () => {
            requestAnimationFrame(() => {
              const img = map.getCanvas().toDataURL();

              current.onDone(img);

              currentRef.current = null;
              setCurrent(null);

              process();
            });
          });
        }}
      >
        <Source id={sourceId} type="geojson" data={current.geojson}>
          {current.geojson.geometry.type === "Point" ? (
            <Layer
              id={pointId}
              type="circle"
              source={sourceId}
              paint={{
                "circle-radius": 6,
                "circle-color": current.color || "#e66a25",
                "circle-opacity": POLYGON_OPACITY,
                "circle-stroke-color": current.color || "#e66a25",
                "circle-stroke-width": POLYGON_STROKE_WIDTH,
              }}
            />
          ) : (
            <>
              <Layer
                id={layerId}
                type="fill"
                source={sourceId}
                paint={{
                  "fill-color": current.color || "#e66a25",
                  "fill-opacity": POLYGON_OPACITY,
                }}
              />
              <Layer
                id={outlineId}
                type="line"
                source={sourceId}
                paint={{
                  "line-color": current.color || "#e66a25",
                  "line-width": POLYGON_STROKE_WIDTH,
                }}
              />
            </>
          )}
        </Source>
      </Map>
    </div>
  );
};
